import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { useGetLesson, useGetLessons, useGetProgress, useGetSpace } from '@onesource/services';
import { stripHtml } from '@onesource/utils';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

import { LessonDetailSkeleton } from '#/features/community-hub/components/lesson-detail-skeleton';
import { LessonDetailView } from '#/features/community-hub/components/lesson-detail-view';
import {
  NavBreadcrumbCurrent,
  NavBreadcrumbLabel,
  NavBreadcrumbLink,
  NavBreadcrumbs,
} from '#/features/community-hub/components/nav-breadcrumbs';
import { StickyNavRow } from '#/features/community-hub/components/sticky-nav-row';
import { useContentRoute } from '#/features/community-hub/hooks/use-content-route';
import { segmentForBlock } from '#/features/community-hub/utils/content-urls';

export const Route = createFileRoute('/_protected/programs/$programSlug/$moduleSlug/$lessonSlug/')({
  component: LessonDetailRoute,
});

function LessonDetailRoute() {
  const {
    programId,
    programSlug,
    subSpaceId = '',
    subSpaceSlug = '',
    lessonId = '',
    lessonSlug = '',
  } = useContentRoute();
  const navigate = useNavigate();

  const { data: lesson, isLoading: isLoadingLesson, error: lessonError } = useGetLesson(lessonId);
  const { data: lessonProgress, isLoading: isLoadingProgress } = useGetProgress(lessonId);
  const { data: module, isLoading: isLoadingModule } = useGetSpace(subSpaceId);
  const { data: lessons, isLoading: isLoadingLessons } = useGetLessons(subSpaceId);
  const findParentLesson = lessons?.find(l => l.id === lesson?.parentId);

  // Parent-type lessons (those that contain sub-lessons) have no detail view of
  // their own — bounce to the module page where they render as a section.
  const isParentLesson = !!lesson && ((lesson.subLessonCount || 0) > 0 || (lesson.children?.length ?? 0) > 0);

  useEffect(() => {
    if (isParentLesson) {
      navigate({
        to: '/programs/$programSlug/$moduleSlug',
        params: { programSlug, moduleSlug: subSpaceSlug },
        replace: true,
      });
    }
  }, [isParentLesson, navigate, programSlug, subSpaceSlug]);

  if (isLoadingLesson || isLoadingProgress || isLoadingModule || isLoadingLessons || isParentLesson) {
    return <LessonDetailSkeleton />;
  }

  if (lessonError) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load lesson: {lessonError.message}</Alert>
      </Box>
    );
  }

  if (!lesson) {
    return (
      <Box p={3}>
        <Alert severity="error">Lesson not found</Alert>
      </Box>
    );
  }

  // Mobile back target: the module page, opened on the section this lesson
  // belongs to when it sits under a parent lesson.
  const backLabel = findParentLesson?.title || stripHtml(module?.name) || undefined;

  const breadcrumbs = (
    <StickyNavRow backHref={`/programs/${programSlug}/${subSpaceSlug}`} backLabel={backLabel}>
      <NavBreadcrumbs>
        {module?.parent && (
          <NavBreadcrumbLink to="/programs/$programSlug" params={{ programSlug }}>
            <NavBreadcrumbLabel html={module.parent.name} />
          </NavBreadcrumbLink>
        )}
        {module && (
          <NavBreadcrumbLink to="/programs/$programSlug/$moduleSlug" params={{ programSlug, moduleSlug: subSpaceSlug }}>
            <NavBreadcrumbLabel html={module.name} />
          </NavBreadcrumbLink>
        )}
        {findParentLesson && (
          <NavBreadcrumbLink
            to="/programs/$programSlug/$moduleSlug"
            params={{ programSlug, moduleSlug: subSpaceSlug }}
            search={{ sectionId: findParentLesson.id }}
          >
            {findParentLesson.title}
          </NavBreadcrumbLink>
        )}
        <NavBreadcrumbCurrent>{lesson.title}</NavBreadcrumbCurrent>
      </NavBreadcrumbs>
    </StickyNavRow>
  );

  return (
    <LessonDetailView
      lesson={lesson}
      progressPercent={lessonProgress?.progressPercent ?? 0}
      breadcrumbs={breadcrumbs}
      onNavigateToBlock={block => {
        const segment = segmentForBlock(block);
        if (!segment) return;
        // Navigate straight to the slug item URL; fall back to the legacy UUID
        // viewer (which redirects) when the block has no slug yet.
        if (block.urlSlug) {
          navigate({ to: `/programs/${programSlug}/${subSpaceSlug}/${lessonSlug}/${segment}/${block.urlSlug}` });
          return;
        }
        navigate({
          to: `/community-hub/spaces/${programId}/modules/${subSpaceId}/lessons/${lessonId}/${segment}/${block.id}`,
        });
      }}
      onNavigateToSubLesson={subLessonId => {
        navigate({ to: `/community-hub/spaces/${programId}/modules/${subSpaceId}/lessons/${subLessonId}` });
      }}
    />
  );
}
